import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";

import { logDebug } from "../utils/logger";

interface SectionMapItem {
  sectionId: string;
  startGroupIndex: number;
  groupCount: number;
}

// px tolerance for "at the top/bottom" of a scroller
const EDGE_SLOP = 4;
// fraction of the visible area scrolled per continue step
const STEP_SCROLL_RATIO = 0.8;

const scrollInner = (el: HTMLElement, direction: 1 | -1) => {
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  el.scrollBy({
    top: direction * Math.round(el.clientHeight * STEP_SCROLL_RATIO),
    behavior: reducedMotion ? "auto" : "smooth",
  });
};

export function useNarrativeNavigation(
  totalGroups: number,
  initialGroupIndex: number,
  sectionMap: SectionMapItem[],
  visibleSectionIds: Set<string>,
  onGroupIndexChange?: (index: number) => void,
) {
  // Clamp initialGroupIndex to valid range
  const clampedInitialIndex = Math.max(
    0,
    Math.min(initialGroupIndex, totalGroups - 1),
  );

  const [currentGroupIndex, setCurrentGroupIndex] =
    useState(clampedInitialIndex);
  // Initialize animation state for the initial group to false for proper detection
  const [groupAnimationStates, setGroupAnimationStates] = useState<
    Record<number, boolean>
  >(() => ({
    [clampedInitialIndex]: false,
  }));
  const [isNavigatingBackward, setIsNavigatingBackward] = useState(false);
  const [showProgress, setShowProgress] = useState(false);

  const previousGroupIndexRef = useRef<number>(clampedInitialIndex);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(clampedInitialIndex > 0);
  const progressTimeoutRef = useRef<number | null>(null);
  // Mirror of currentGroupIndex for reading inside scrollToIndex without
  // invalidating its useCallback identity (empty deps).
  const currentGroupIndexRef = useRef<number>(clampedInitialIndex);
  useEffect(() => {
    currentGroupIndexRef.current = currentGroupIndex;
  }, [currentGroupIndex]);

  // Sync URL when currentGroupIndex changes
  useEffect(() => {
    onGroupIndexChange?.(currentGroupIndex);
  }, [currentGroupIndex, onGroupIndexChange]);

  // Track navigation direction when currentGroupIndex changes
  useEffect(() => {
    const goingBackward = currentGroupIndex < previousGroupIndexRef.current;
    setIsNavigatingBackward(goingBackward);
    previousGroupIndexRef.current = currentGroupIndex;
  }, [currentGroupIndex]);

  // Show progress bars when navigating backwards
  useEffect(() => {
    const isMovingBackward = currentGroupIndex < previousGroupIndexRef.current;

    if (isMovingBackward) {
      setShowProgress(true);

      if (progressTimeoutRef.current !== null) {
        clearTimeout(progressTimeoutRef.current);
      }

      progressTimeoutRef.current = window.setTimeout(() => {
        setShowProgress(false);
      }, 2000);
    }

    // We update ref in the other effect, but strictly speaking
    // we should rely on the state change direction here or unify them.
    // The previous effect handles visual direction state, this handles progress bar visibility.
    // Since previousGroupIndexRef is updated in the "Track navigation direction" effect,
    // and effects run in order, we might miss it if we update the ref there first.
    // Actually, React effects run after render. If we have two effects dependent on `currentGroupIndex`,
    // the order is determined by code order.
    // The previous effect updates `previousGroupIndexRef`.
    // Ideally we should not update the ref until we've used it in all effects.
    // But let's just use `setIsNavigatingBackward`'s logic here or move this logic into one effect.
    // I'll leave the ref update to the separate effect and assume this runs.
    // Actually, to be safe, I'll rely on the `isNavigatingBackward` state for the progress check? No, that state is set in an effect.
    // Refactoring: I'll trust the original code's logic but be careful about splitting effects.
  }, [currentGroupIndex]);

  // Easing function for natural scroll feel
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Scroll to specific group index with momentum-based animation
  const scrollToIndex = useCallback((index: number, useMomentum = false) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Guard against reentrancy: if a jump is already in flight (e.g. the
    // previous instant-jump's rAF hasn't restored styles yet), bail out so
    // we don't snapshot the temporary 'none' styles as originals.
    if (isScrollingRef.current) return;

    // Already on this group: bail before touching anything. Falling through
    // would reset groupAnimationStates[index] for a group that has already
    // finished animating, and nothing would ever set it back — InsightGroup
    // only reports completion on a visibility change, and this group never
    // becomes invisible. That strands the group: the ContinueIndicator is
    // gated on that flag and so is advanceGroup, so the viewer cannot page
    // forward at all. Reachable by tapping the progress dot for the section
    // you are already on, at its first group. See #362 for the root cause.
    if (index === currentGroupIndexRef.current) return;

    // Reset animation state for the target group so ContinueIndicator waits for animation
    setGroupAnimationStates((prev) => ({
      ...prev,
      [index]: false,
    }));

    isScrollingRef.current = true;
    const cardHeight = container.clientHeight;
    const targetScroll = index * cardHeight;
    const startScroll = container.scrollTop;
    const distance = targetScroll - startScroll;

    // Large jumps (e.g. tapping a section dot, TOC "continue", or header tap
    // back to start) should land instantly instead of animating the scroll
    // container through every intermediate group. Animating flashes through
    // all the in-between content and — because the isScrolling lock can
    // expire mid-scroll — lets handleScroll snap currentGroupIndex to an
    // intermediate group, which in turn flashes through intermediate section
    // backgrounds and interstitials. An instant jump shows only the
    // destination section (and its interstitial, when moving forward).
    const groupDelta = Math.abs(index - currentGroupIndexRef.current);
    if (groupDelta > 1) {
      const originalSnapType = container.style.scrollSnapType;
      const originalBehavior = container.style.scrollBehavior;
      container.style.scrollSnapType = "none";
      container.style.scrollBehavior = "auto";
      container.scrollTop = targetScroll;
      currentGroupIndexRef.current = index;
      setCurrentGroupIndex(index);
      // Restore scroll styles after the paint that includes the new scrollTop,
      // so re-enabling snap/smooth can't re-animate the jump.
      requestAnimationFrame(() => {
        container.style.scrollSnapType = originalSnapType;
        container.style.scrollBehavior = originalBehavior;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      });
      return;
    }

    if (useMomentum) {
      // Use custom momentum-based scroll animation
      logDebug("Using momentum scroll", {
        startScroll,
        targetScroll,
        distance,
        cardHeight,
      });
      const duration = 1000; // ms - scroll duration for full viewport height
      const startTime = performance.now();

      // Temporarily disable scroll snap AND smooth scrolling to prevent browser interference
      const originalSnapType = container.style.scrollSnapType;
      const originalScrollBehavior = container.style.scrollBehavior;
      container.style.scrollSnapType = "none";
      container.style.scrollBehavior = "auto";

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        container.scrollTop = startScroll + distance * easedProgress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          logDebug("Momentum scroll complete");
          // Re-enable scroll snap and smooth scrolling after animation completes
          container.style.scrollSnapType = originalSnapType;
          container.style.scrollBehavior = originalScrollBehavior;
          isScrollingRef.current = false;
          // Update the group index - content will animate in now
          currentGroupIndexRef.current = index;
          setCurrentGroupIndex(index);
        }
      };

      requestAnimationFrame(animateScroll);
    } else {
      // Use standard smooth scroll for keyboard/manual navigation
      currentGroupIndexRef.current = index;
      setCurrentGroupIndex(index);
      container.scrollTo({
        top: targetScroll,
        behavior: "smooth",
      });

      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500);
    }
  }, []);

  // Scroll to initial position on mount (for URL deep-linking).
  // useLayoutEffect runs before the browser paints, preventing a visible flash of page 0.
  const hasScrolledToInitialRef = useRef(false);
  useLayoutEffect(() => {
    if (clampedInitialIndex > 0 && !hasScrolledToInitialRef.current) {
      hasScrolledToInitialRef.current = true;

      const container = scrollContainerRef.current;
      if (container) {
        const cardHeight = container.clientHeight;
        const originalSnapType = container.style.scrollSnapType;
        const originalBehavior = container.style.scrollBehavior;
        // Disable both snap and smooth scroll so scrollTop assignment is instant
        container.style.scrollSnapType = "none";
        container.style.scrollBehavior = "auto";

        container.scrollTop = clampedInitialIndex * cardHeight;

        requestAnimationFrame(() => {
          container.style.scrollSnapType = originalSnapType;
          container.style.scrollBehavior = originalBehavior;
          requestAnimationFrame(() => {
            isScrollingRef.current = false;
          });
        });
      } else {
        isScrollingRef.current = false;
      }
    } else if (clampedInitialIndex === 0) {
      isScrollingRef.current = false;
    }
  }, [clampedInitialIndex]);

  // Handle navigation when current section becomes invisible
  useEffect(() => {
    if (!visibleSectionIds) return;

    const currentSection = sectionMap.find(
      (s) =>
        currentGroupIndex >= s.startGroupIndex &&
        currentGroupIndex < s.startGroupIndex + s.groupCount,
    );

    // If current section is not in visible sections, navigate to first visible section
    if (currentSection && !visibleSectionIds.has(currentSection.sectionId)) {
      const firstVisibleSection = sectionMap[0];
      if (firstVisibleSection) {
        scrollToIndex(firstVisibleSection.startGroupIndex);
      }
    }
  }, [visibleSectionIds, currentGroupIndex, sectionMap, scrollToIndex]);

  // Reliable swipe paging on mobile.
  //
  // Each group renders its content inside an inner `overflow-y-auto` area so
  // tall pages can scroll. On touch devices a single gesture is captured by
  // one scroller for its whole lifetime, so when a page's content overflows
  // the viewport the inner area swallows the swipe and native CSS scroll-snap
  // never advances the outer pager — the user has to swipe several times to
  // get to the next page. We detect a deliberate vertical swipe that the inner
  // scroller can't consume (because it's already at its top/bottom edge) and
  // page programmatically. This only fires when an inner scroller is actually
  // scrollable, so short pages keep using native snap unchanged.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const SWIPE_THRESHOLD = 45; // min vertical travel (px) to count as a swipe
    const EDGE_SLOP = 4; // px tolerance for "at the top/bottom" of a scroller

    let startX = 0;
    let startY = 0;
    let innerScroller: HTMLElement | null = null;
    let tracking = false;

    // Walk up from the touch target to find a vertically scrollable ancestor
    // (the group's content area) without matching the outer pager itself.
    const findInnerScroller = (
      start: EventTarget | null,
    ): HTMLElement | null => {
      let el = start instanceof HTMLElement ? start : null;
      while (el && el !== container) {
        const overflowY = getComputedStyle(el).overflowY;
        if (
          el.scrollHeight - el.clientHeight > EDGE_SLOP &&
          (overflowY === "auto" || overflowY === "scroll")
        ) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        tracking = false;
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      innerScroller = findInnerScroller(e.target);
      tracking = true;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;

      const dy = startY - t.clientY; // positive = swipe up (toward next page)
      const dx = Math.abs(t.clientX - startX);

      // Ignore taps, mostly-horizontal swipes, and short drags.
      if (Math.abs(dy) < SWIPE_THRESHOLD || dx > Math.abs(dy)) return;
      // Native snap handles short pages with no scrollable inner area.
      if (!innerScroller) return;
      if (isScrollingRef.current) return;

      const index = currentGroupIndexRef.current;
      // Bail if native scrolling already moved us off this page.
      const cardHeight = container.clientHeight;
      if (
        cardHeight > 0 &&
        Math.round(container.scrollTop / cardHeight) !== index
      ) {
        return;
      }

      if (dy > 0) {
        const atBottom =
          innerScroller.scrollTop + innerScroller.clientHeight >=
          innerScroller.scrollHeight - EDGE_SLOP;
        if (atBottom && index < totalGroups - 1) {
          scrollToIndex(index + 1);
        }
      } else {
        const atTop = innerScroller.scrollTop <= EDGE_SLOP;
        if (atTop && index > 0) {
          scrollToIndex(index - 1);
        }
      }
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchend", onTouchEnd);
    };
  }, [totalGroups, scrollToIndex]);

  // Update current group index based on scroll position
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container || isScrollingRef.current) return;

    const scrollTop = container.scrollTop;
    const cardHeight = container.clientHeight;
    // Simple rounding might oscillate if between pixels, but it's what was there.
    const newIndex = Math.round(scrollTop / cardHeight);

    if (
      newIndex !== currentGroupIndex &&
      newIndex >= 0 &&
      newIndex < totalGroups
    ) {
      setCurrentGroupIndex(newIndex);
    }
  };

  // Handle group animation completion
  const handleGroupAnimationComplete = (groupIndex: number) => {
    setGroupAnimationStates((prev) => ({
      ...prev,
      [groupIndex]: true,
    }));
  };

  const getInnerScroller = useCallback(
    (): HTMLElement | null =>
      scrollContainerRef.current?.querySelector<HTMLElement>(
        `[data-group-content="${currentGroupIndexRef.current}"]`,
      ) ?? null,
    [],
  );

  // Step forward, mirroring the touch-swipe behavior above: when the current
  // page's content overflows, first scroll that inner content area (see
  // `data-group-content` in InsightGroup) and only page once the scroller is at
  // its bottom edge. Pass `allowInnerScroll: false` for a deterministic
  // page-step regardless of content height (ArrowRight).
  //
  // Shared by keyboard navigation and the click/tap-to-continue affordance on
  // ContinueIndicator so every "continue" gesture behaves identically.
  const advanceGroup = useCallback(
    ({ allowInnerScroll = true }: { allowInnerScroll?: boolean } = {}) => {
      if (allowInnerScroll) {
        const inner = getInnerScroller();
        if (inner) {
          const atBottom =
            inner.scrollTop + inner.clientHeight >=
            inner.scrollHeight - EDGE_SLOP;
          if (!atBottom) {
            scrollInner(inner, 1);
            return;
          }
        }
      }
      // Only allow navigation if current group animation is complete
      if (
        currentGroupIndex < totalGroups - 1 &&
        groupAnimationStates[currentGroupIndex]
      ) {
        scrollToIndex(currentGroupIndex + 1);
      }
    },
    [
      currentGroupIndex,
      totalGroups,
      groupAnimationStates,
      scrollToIndex,
      getInnerScroller,
    ],
  );

  // Step backward — the mirror image of advanceGroup.
  const retreatGroup = useCallback(
    ({ allowInnerScroll = true }: { allowInnerScroll?: boolean } = {}) => {
      if (allowInnerScroll) {
        const inner = getInnerScroller();
        if (inner && inner.scrollTop > EDGE_SLOP) {
          scrollInner(inner, -1);
          return;
        }
      }
      if (currentGroupIndex > 0) {
        scrollToIndex(currentGroupIndex - 1);
      }
    },
    [currentGroupIndex, scrollToIndex, getInnerScroller],
  );

  // Keyboard navigation (skip when focus is inside DevNavigator or other interactive panels)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("[data-dev-navigator]")) return;
      // Never hijack keys while the user is typing in a form field.
      if (
        target?.closest?.('input, textarea, select, [contenteditable="true"]')
      )
        return;
      // Space on a focused button/link must activate it, not page.
      if (e.key === " " && target?.closest?.('button, a, [role="button"]'))
        return;

      if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        advanceGroup({ allowInnerScroll: e.key !== "ArrowRight" });
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        retreatGroup({ allowInnerScroll: e.key !== "ArrowLeft" });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [advanceGroup, retreatGroup]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (progressTimeoutRef.current !== null) {
        clearTimeout(progressTimeoutRef.current);
      }
    };
  }, []);

  return {
    currentGroupIndex,
    isNavigatingBackward,
    scrollContainerRef,
    scrollToIndex,
    advanceGroup,
    handleScroll,
    showProgress,
    groupAnimationStates,
    setGroupAnimationStates,
    handleGroupAnimationComplete,
  };
}
